import { KeyRound, Lock } from "lucide-react";
import { safeReturnTo } from "@zworks/console-contracts";
import { Link, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * The sign-in page, as the Login board draws it. There is deliberately no password field:
 * the person signs in through their organisation's identity provider. Signing in creates
 * the account on first use. Nothing here handles a token: the form leaves the page for
 * `/api/v1/auth/login`, which redirects to the provider, and the session travels only in
 * the `HttpOnly` cookie.
 */
export function LoginPage() {
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get("returnTo"));
  // The return path travels in a hidden field, not the query string: a GET form used to put
  // it, and the work email, in the address bar and in browser history.
  const loginAction = "/api/v1/auth/login";
  return (
    <div className="flex min-h-svh flex-col bg-background">
      <header className="flex h-12 shrink-0 items-center gap-3 border-b px-4">
        <span className="flex items-center gap-2 text-sm font-semibold">
          <span
            aria-hidden
            className="grid size-6 place-items-center rounded-md bg-primary text-xs font-semibold text-primary-foreground"
          >
            Z
          </span>
          Zworks
        </span>
        <span className="ml-auto" />
        <Link
          to="/help"
          className="inline-flex h-11 min-w-11 items-center justify-center px-2 text-sm text-muted-foreground hover:text-foreground md:h-auto md:min-w-0 md:px-0"
        >
          Help
        </Link>
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="flex w-full max-w-sm flex-col gap-6">
          <div className="flex flex-col gap-2 text-center">
            <h1 className="max-w-full text-[1.75rem] leading-tight font-semibold tracking-tight [overflow-wrap:anywhere]">
              Log in to Zworks
            </h1>
          </div>

          <form
            aria-label="Sign in with SSO"
            action={loginAction}
            method="post"
            className="flex flex-col gap-3"
          >
            {returnTo === null ? null : <input type="hidden" name="returnTo" value={returnTo} />}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="login-email" className="text-sm font-medium text-foreground">
                Work email
              </label>
              <Input
                id="login-email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="name@company.com"
                required
              />
            </div>
            <Button size="lg" className="w-full" type="submit">
              <KeyRound aria-hidden />
              Continue with SSO
            </Button>
            <p className="text-center text-[0.8125rem] text-muted-foreground">
              We use your email domain to find your organisation&apos;s sign-in page.
            </p>

            <div className="flex items-center gap-3 text-xs text-muted-foreground" aria-hidden>
              <span className="h-px flex-1 bg-border" />
              <span>or</span>
              <span className="h-px flex-1 bg-border" />
            </div>

            <div className="flex flex-col gap-2">
              <ProviderButton
                label="Continue with Google"
                mark="G"
                provider="google"
                returnTo={returnTo}
              />
              <ProviderButton
                label="Continue with Microsoft"
                mark="M"
                provider="microsoft"
                returnTo={returnTo}
              />
              <ProviderButton
                label="Continue with GitHub"
                mark="GH"
                provider="github"
                returnTo={returnTo}
              />
            </div>
          </form>

          <p className="flex items-start justify-center gap-2 text-center text-xs text-muted-foreground">
            <Lock aria-hidden className="mt-0.5 size-3.5 shrink-0" />
            <span>
              Zworks never sees your password. Your identity provider confirms who you are and sends
              us your name and work email.
            </span>
          </p>
        </div>
      </main>

      <footer className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 border-t px-4 py-3 text-xs text-muted-foreground">
        {/* There is no terms, privacy or status page to link to yet, so these are plain text
            rather than links that go nowhere, and the agreement claim is gone with them. A
            checkbox or a footer sentence that says you agree to a document you cannot open is a
            worse defect than a missing link. Restore both once the pages exist. */}
        <span>Terms of use and Privacy policy are published separately.</span>
      </footer>
    </div>
  );
}

/**
 * One of the provider buttons below the divider. It submits the same work email, with the
 * provider as a hint; the API still chooses the sign-in page from the email domain.
 * `formNoValidate` lets it submit without an email, while the SSO button keeps native
 * validation on the required field.
 */
function ProviderButton({
  label,
  mark,
  provider,
  returnTo,
}: {
  label: string;
  mark: string;
  provider: string;
  returnTo: string | null;
}) {
  const params = new URLSearchParams({ provider });
  if (returnTo !== null) params.set("returnTo", returnTo);
  return (
    <Button
      variant="outline"
      size="lg"
      className="w-full justify-start"
      type="submit"
      formNoValidate
      formAction={`/api/v1/auth/login?${params.toString()}`}
    >
      <span
        aria-hidden
        className="grid size-6 shrink-0 place-items-center rounded-md bg-secondary text-xs font-semibold text-secondary-foreground"
      >
        {mark}
      </span>
      <span className="flex-1">{label}</span>
    </Button>
  );
}
